import { useOrganization } from '@clerk/clerk-react';
import { UNIT_NAMES, UNITS } from '@flobi/artwork-core';
import {
  AppSettings,
  SettingsChoiceRow,
  SettingsLinkRow,
  SettingsSwitchRow,
  useSettings,
} from '@flobi/asset-library/account';
import { useEffect, useState } from 'react';
import { addonsApi } from '../editor/addons/api';
import { useViewSettings, type ViewKey } from '../editor/view';
import { brandInForce, useStartBrand } from '../home/Composer';
import { RESOLUTIONS, useNewFileDefaults } from '../home/newFileDefaults';
import { usableBrands } from '../services/work';
import { OPEN_ADDONS } from './events';
import { ADDONS_HIDDEN } from '../lib/hiddenFeatures';
import type { BrandCard } from '../services/work';

/** What the next file starts with, and which brand it is made in. */
function NewFileRows() {
  const [defaults, change] = useNewFileDefaults();
  const { organization } = useOrganization();
  const [brandId, setBrandId] = useStartBrand(organization?.id ?? '');
  const [brands, setBrands] = useState<BrandCard[] | null>(null);
  useEffect(() => {
    let live = true;
    usableBrands()
      .then((list) => live && setBrands(list))
      .catch(() => live && setBrands([]));
    return () => {
      live = false;
    };
  }, [organization?.id]);
  const inForce = brandInForce(brands, brandId);

  return (
    <>
      <SettingsChoiceRow
        label="Units for new files"
        value={defaults.units}
        options={UNITS.map((unit) => ({ id: unit, label: UNIT_NAMES[unit] }))}
        onChange={(id) => change({ units: id as (typeof UNITS)[number] })}
      />
      <SettingsChoiceRow
        label="Colour for new files"
        value={defaults.colorMode}
        options={[
          { id: 'rgb', label: 'RGB, for screens' },
          { id: 'cmyk', label: 'CMYK, for print' },
        ]}
        onChange={(id) => change({ colorMode: id as 'rgb' | 'cmyk' })}
      />
      <SettingsChoiceRow
        label="Resolution for new files"
        value={String(defaults.resolution)}
        options={RESOLUTIONS.map((ppi) => ({ id: String(ppi), label: `${ppi} ppi` }))}
        onChange={(id) => change({ resolution: Number(id) })}
      />
      {brands && brands.length > 0 ? (
        <SettingsChoiceRow
          label="Brand for new files"
          value={inForce?.id ?? 'none'}
          options={[...brands.map((one) => ({ id: one.id, label: one.name })), { id: 'none', label: 'No brand' }]}
          onChange={setBrandId}
        />
      ) : null}
    </>
  );
}

const VIEW_ROWS: ReadonlyArray<readonly [ViewKey, string]> = [
  ['rulers', 'Rulers'],
  ['grid', 'Grid'],
  ['transparency', 'Transparency grid'],
  ['guides', 'Guides'],
  ['smart', 'Smart guides'],
  ['snapPoint', 'Snap to points'],
  ['snapGrid', 'Snap to grid'],
  ['snapPixel', 'Snap to pixels'],
];

/** What is shown and what a drag is pulled onto: the editor's View menu, kept in this browser. */
function ViewRows() {
  const { view, set } = useViewSettings();
  return (
    <>
      {VIEW_ROWS.map(([key, label]) => (
        <SettingsSwitchRow key={key} label={label} on={view[key]} onChange={(on) => set(key, on)} />
      ))}
    </>
  );
}

/**
 * The workspace's add-ons: how many there are, and the way to the panel that manages them. The panel lives
 * inside a file, so outside one the row is left out rather than shown greyed with a reason (omit, never grey).
 */
function AddonRows() {
  const { close } = useSettings();
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    addonsApi
      .list()
      .then((items) => live && setCount(items.length))
      .catch(() => live && setCount(null));
    return () => {
      live = false;
    };
  }, []);
  return (
    <SettingsLinkRow
      label="Add-ons"
      value={count === null ? undefined : String(count)}
      onClick={() => {
        close();
        window.setTimeout(() => window.dispatchEvent(new Event(OPEN_ADDONS)), 60);
      }}
    />
  );
}

// One object for the life of the page, so the window's registration is made once.
const SECTIONS = {
  forYou: (
    <>
      <NewFileRows />
      <ViewRows />
    </>
  ),
  // Read as the window draws, so it follows whether a file is open; an empty section is left out.
  lists: () => (!ADDONS_HIDDEN && window.location.pathname.startsWith('/f/') ? <AddonRows /> : null),
  notifications: true as const,
  access: true as const,
};

/** Artwork's page in the Settings window. Draws nothing itself. */
export function ArtworkSettings() {
  return <AppSettings app="artwork" label="Artwork" sections={SECTIONS} />;
}
